import { useState, useEffect, useCallback } from 'react';
import { IEvent, IShow, IBreedGroupJudge, IBreedJudge } from '@eagerdog/interfaces';
import {
  Constants,
  getRunningTimeMinutes,
  isUKCConformationUnscheduledLevel,
  syncUKCConformationBISTiles,
} from '@eagerdog/constants';
import { apiService } from 'src/services';

/**
 * Minimal running-order judge shape used by this hook.
 * The main JudgeSchedulesTab component uses IRunningOrderDrag (which extends IRunningOrder),
 * but the hook only needs these fields to distribute tiles.
 */
interface IRunningOrderBase {
  judge_name: string;
  judge_number?: string;
  total_hours: number;
  dog_classes: any[];
  [key: string]: any;
}

type JudgeKey = string; // "judge_name||judge_number"

const makeJudgeKey = (name: string, number: string): JudgeKey => `${name}||${number}`;

/**
 * Hook that owns all UKC Conformation-specific judge schedule logic:
 * - Fetching breed/group entry counts from the API
 * - Building the initial judges array with tiles distributed by the hierarchy:
 *     1. Breed-specific tiles  → breed judge column
 *     2. Breed group tiles     → breed group judge column (fallback: element judge)
 *     3. Special element tiles → element judge column (Junior Showmanship, Brace, etc.)
 * - Keeping special element tiles in sync as scores change
 *
 * Novice Puppy and Veteran entries are counted in their breed's tile, and Total Dog /
 * Total Junior are fee-only, so none of them get a tile of their own.
 *
 * Only touches UKC Conformation shows. Safe to call for any show — returns empty
 * data and no-op functions for all other show types.
 */
export const useUKCConformationSchedule = (
  event: IEvent,
  show: IShow,
  allScores: any[]
) => {
  const [ukcConformationCounts, setUkcConformationCounts] = useState<Map<string, any>>(new Map());
  const [ukcConformationCountsLoaded, setUkcConformationCountsLoaded] = useState(false);

  const isUKCConformation =
    event.sanctioning_club === Constants.sanctioning_club.UKC &&
    show.show_type === Constants.show_type.conformation;

  // ─── Fetch counts ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isUKCConformation) return;

    apiService
      .getUKCConformationCounts(event._id, show._id)
      .then((response: any) => {
        if (!response || !Array.isArray(response)) {
          setUkcConformationCounts(new Map());
          return;
        }

        const countsMap = new Map<string, any>();
        response.forEach((count: any) => {
          if (!count?.breed_group || !count?.breed) return;
          const key = `${count.breed_group}-${count.breed}`;
          if (!countsMap.has(key)) {
            countsMap.set(key, {
              male_count: 0, female_count: 0, champion_count: 0, grand_champion_count: 0,
              novice_puppy_count: 0, brace_count: 0, veteran_count: 0,
              alt_male_count: 0, alt_female_count: 0, alt_champion_count: 0,
              alt_grand_champion_count: 0, alt_novice_puppy_count: 0,
              alt_brace_count: 0, alt_veteran_count: 0,
            });
          }
          const existing = countsMap.get(key);
          const numericFields = [
            'male_count', 'female_count', 'champion_count', 'grand_champion_count',
            'novice_puppy_count', 'brace_count', 'veteran_count',
            'alt_male_count', 'alt_female_count', 'alt_champion_count',
            'alt_grand_champion_count', 'alt_novice_puppy_count',
            'alt_brace_count', 'alt_veteran_count',
          ];
          numericFields.forEach((field) => {
            existing[field] = (existing[field] || 0) + (count[field] || 0);
          });
        });

        setUkcConformationCounts(countsMap);
        setUkcConformationCountsLoaded(true);
      })
      .catch(() => {
        setUkcConformationCounts(new Map());
        setUkcConformationCountsLoaded(true);
      });
  }, [event._id, show._id, isUKCConformation]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const sumEntries = (c: any): number =>
    (c.male_count || 0) + (c.female_count || 0) +
    (c.champion_count || 0) + (c.grand_champion_count || 0) +
    (c.novice_puppy_count || 0) + (c.brace_count || 0) + (c.veteran_count || 0) +
    (c.alt_male_count || 0) + (c.alt_female_count || 0) +
    (c.alt_champion_count || 0) + (c.alt_grand_champion_count || 0) +
    (c.alt_novice_puppy_count || 0) + (c.alt_brace_count || 0) + (c.alt_veteran_count || 0);

  // ─── buildInitialJudges ─────────────────────────────────────────────────────
  /**
   * Builds the complete initial judges array for a fresh/reset UKC Conformation schedule.
   *
   * Tile types created:
   *   A — Breed group tiles (entries minus any breed-specific entries)
   *   B — Breed-specific tiles (only for breeds with at least one entry)
   *   C — Special element tiles (Junior Showmanship, Brace, etc.)
   *
   * Only call this when all currentJudges have empty dog_classes.
   */
  const buildInitialJudges = useCallback(
    (currentJudges: IRunningOrderBase[]): IRunningOrderBase[] => {
      if (!isUKCConformation || !ukcConformationCountsLoaded || currentJudges.length === 0) {
        return currentJudges;
      }

      const runningTimePerDog = getRunningTimeMinutes(
        event.sanctioning_club,
        show.show_type,
        undefined,
        undefined
      );

      const breedGroupJudges: IBreedGroupJudge[] = show.breed_group_judges || [];
      const breedJudges: IBreedJudge[] = show.breed_judges || [];
      const showElements: any[] = show.show_elements || [];

      const fallbackJudge = {
        judge_name: currentJudges[0].judge_name || '',
        judge_number: currentJudges[0].judge_number || '',
      };

      // IMPORTANT: Only allow judges that are actually in the current ring.
      // breed_group_judges and breed_judges are show-level, so a judge assigned
      // to a breed group in Ring 2 must NOT be introduced into Ring 1.
      const validJudgeKeys = new Set<JudgeKey>(
        currentJudges.map((j) => makeJudgeKey(j.judge_name, j.judge_number || ''))
      );

      const isValidJudge = (j: { judge_name: string; judge_number: string }): boolean =>
        validJudgeKeys.has(makeJudgeKey(j.judge_name, j.judge_number));

      // Determine if this ring's primary judge is a dedicated breed group judge.
      // Breed group judge rings should ONLY receive tiles assigned to their judge —
      // unassigned breed groups belong in the element judge's ring.
      const fallbackKey = makeJudgeKey(fallbackJudge.judge_name, fallbackJudge.judge_number);
      const isBreedGroupJudgeRing = breedGroupJudges.some(
        (bgj) =>
          bgj.judge_name &&
          makeJudgeKey(bgj.judge_name, bgj.judge_number || '') === fallbackKey
      );
      // For breed group judge rings, unassigned groups should NOT fall to this ring's judge.
      // For element judge rings, unassigned groups fall to the element judge (fallback).
      const effectiveFallback = isBreedGroupJudgeRing ? null : fallbackJudge;

      // Breeds that have a breed-specific judge — excluded from group tiles
      const breedSpecificSet = new Set<string>(breedJudges.map((bj) => bj.breed));

      // Map of "judgeKey" → { judge info, tiles array }
      const judgeSlots = new Map<JudgeKey, { judge: { judge_name: string; judge_number: string }; tiles: any[] }>();

      const ensureSlot = (judge: { judge_name: string; judge_number: string }) => {
        const k = makeJudgeKey(judge.judge_name, judge.judge_number);
        if (!judgeSlots.has(k)) judgeSlots.set(k, { judge, tiles: [] });
        return judgeSlots.get(k)!;
      };

      // Fallback judge is always first
      ensureSlot(fallbackJudge);

      // Returns the judge for a breed group, or null to indicate "skip this tile in this ring".
      // null is returned when:
      //   - The assigned judge exists but is NOT in this ring (belongs to another ring)
      //   - The ring is a dedicated breed group judge ring and the group has no assigned judge
      //     (unassigned groups belong in the element judge's ring, not here)
      const resolveGroupJudge = (breedGroup: string): { judge_name: string; judge_number: string } | null => {
        const bgj = breedGroupJudges.find((j) => j.breed_group === breedGroup);
        if (bgj?.judge_name) {
          const candidate = { judge_name: bgj.judge_name, judge_number: bgj.judge_number || '' };
          if (isValidJudge(candidate)) return candidate;
          // Assigned judge is in a different ring — skip this tile here
          return null;
        }
        // No assigned judge — goes to element judge (or skip if this is a breed group judge ring)
        return effectiveFallback;
      };

      const resolveBreedJudge = (breed: string): { judge_name: string; judge_number: string } | null => {
        const bj = breedJudges.find((j) => j.breed === breed);
        if (bj?.judge_name) {
          const candidate = { judge_name: bj.judge_name, judge_number: bj.judge_number || '' };
          if (isValidJudge(candidate)) return candidate;
          // Assigned judge is in a different ring — skip
          return null;
        }
        return effectiveFallback;
      };

      const resolveElementJudge = (se: any): { judge_name: string; judge_number: string } | null => {
        if (se.judge_name) {
          const candidate = { judge_name: se.judge_name, judge_number: se.judge_number || '' };
          if (isValidJudge(candidate)) return candidate;
          // Judge is in another ring; skip element tile here only if this is a breed group ring
          if (isBreedGroupJudgeRing) return null;
        }
        return effectiveFallback;
      };

      let tileIdx = 0;

      // ── Type B: Breed-specific tiles ────────────────────────────────────────
      const breedTotals = new Map<string, number>();
      ukcConformationCounts.forEach((breedCounts, key) => {
        // key = "breedGroup-breed". breedGroup has no hyphens so split('-')[0] is safe.
        const breedGroup = key.split('-')[0];
        const breed = key.substring(breedGroup.length + 1);
        if (!breedSpecificSet.has(breed)) return;
        breedTotals.set(breed, (breedTotals.get(breed) || 0) + sumEntries(breedCounts));
      });

      breedJudges.forEach((bj) => {
        const entries = breedTotals.get(bj.breed) || 0;
        if (entries === 0) return;
        const resolvedJudge = resolveBreedJudge(bj.breed);
        if (resolvedJudge === null) return; // belongs to another ring
        const slot = ensureSlot(resolvedJudge);
        slot.tiles.push({
          show_element: Constants.dog_class_element.conformation,
          level: bj.breed,
          entries,
          total_minutes: entries * runningTimePerDog,
          duration: runningTimePerDog,
          type: Constants.running_order_dog_class_type.dog_class,
          index: tileIdx++,
        });
      });

      // ── Type A: Breed group tiles (reduced by breed-specific entries) ────────
      // Count entries per breed (not yet filtered by letter range)
      const breedEntriesByKey = new Map<string, number>(); // key = "breedGroup-breed"
      ukcConformationCounts.forEach((breedCounts, key) => {
        const breedGroup = key.split('-')[0];
        const breed = key.substring(breedGroup.length + 1);
        if (breedSpecificSet.has(breed)) return;
        breedEntriesByKey.set(key, sumEntries(breedCounts));
      });

      // Helper: count entries for a breed group, optionally filtered by alpha range
      const countEntriesForGroup = (breedGroup: string, startLetter?: string, endLetter?: string): number => {
        let total = 0;
        breedEntriesByKey.forEach((count, key) => {
          const kg = key.split('-')[0];
          if (kg !== breedGroup) return;
          if (startLetter && endLetter) {
            const breed = key.substring(kg.length + 1);
            const first = breed[0]?.toUpperCase() ?? 'A';
            if (first < startLetter || first > endLetter) return;
          }
          total += count;
        });
        return total;
      };

      // Group breed_group_judges entries by breed group to detect splits
      const bgJudgesByGroup = new Map<string, IBreedGroupJudge[]>();
      breedGroupJudges.forEach((bgj) => {
        if (!bgJudgesByGroup.has(bgj.breed_group)) bgJudgesByGroup.set(bgj.breed_group, []);
        bgJudgesByGroup.get(bgj.breed_group)!.push(bgj);
      });

      // Follow the canonical UKC breed group order.
      (Constants.ukc_breed_group_order as string[]).forEach((breedGroup) => {
        const groupJudgeEntries = bgJudgesByGroup.get(breedGroup) ?? [];
        const hasConfiguredJudge = groupJudgeEntries.length > 0;
        const totalEntries = countEntriesForGroup(breedGroup);
        if (totalEntries === 0 && !hasConfiguredJudge) return;

        if (groupJudgeEntries.length <= 1) {
          // Unsplit — one tile for the whole group
          const bgj = groupJudgeEntries[0];
          const resolvedJudge = bgj?.judge_name
            ? (() => {
                const candidate = { judge_name: bgj.judge_name, judge_number: bgj.judge_number || '' };
                return isValidJudge(candidate) ? candidate : null;
              })()
            : effectiveFallback;
          if (resolvedJudge === null) return;
          const slot = ensureSlot(resolvedJudge);
          slot.tiles.push({
            show_element: Constants.dog_class_element.conformation,
            level: breedGroup,
            entries: totalEntries,
            total_minutes: totalEntries * runningTimePerDog,
            duration: runningTimePerDog,
            type: Constants.running_order_dog_class_type.dog_class,
            index: tileIdx++,
          });
        } else {
          // Alpha-split — one tile per judge entry, filtered by letter range
          groupJudgeEntries.forEach((bgj) => {
            const entries = countEntriesForGroup(breedGroup, bgj.start_letter, bgj.end_letter);
            const hasJudge = bgj.judge_name && bgj.judge_name.trim() !== '';
            const resolvedJudge = hasJudge
              ? (() => {
                  const candidate = { judge_name: bgj.judge_name, judge_number: bgj.judge_number || '' };
                  return isValidJudge(candidate) ? candidate : null;
                })()
              : effectiveFallback;
            if (resolvedJudge === null) return;
            const slot = ensureSlot(resolvedJudge);
            slot.tiles.push({
              show_element: Constants.dog_class_element.conformation,
              level: breedGroup,
              start_letter: bgj.start_letter,
              end_letter: bgj.end_letter,
              entries,
              total_minutes: entries * runningTimePerDog,
              duration: runningTimePerDog,
              type: Constants.running_order_dog_class_type.dog_class,
              index: tileIdx++,
            });
          });
        }
      });

      // ── Type C: Special element tiles ────────────────────────────────────────
      const conformationElements = new Set([
        Constants.dog_class_element.conformation,
        Constants.dog_class_element.conformation_altered,
      ]);
      const seenElements = new Set<string>();

      showElements.forEach((se: any) => {
        if (!se.show_element || conformationElements.has(se.show_element)) return;
        if (isUKCConformationUnscheduledLevel(se.level)) return;
        const seKey = `${se.show_element}||${se.level || ''}`;
        if (seenElements.has(seKey)) return;
        seenElements.add(seKey);

        const resolvedJudge = resolveElementJudge(se);
        if (resolvedJudge === null) return; // belongs to another ring — skip
        const slot = ensureSlot(resolvedJudge);
        slot.tiles.push({
          show_element: se.show_element,
          level: se.level || '',
          entries: 0,
          total_minutes: 0,
          duration: runningTimePerDog,
          type: Constants.running_order_dog_class_type.dog_class,
          index: tileIdx++,
        });
      });

      // ── Build final judges array ──────────────────────────────────────────────
      // fallbackKey is already declared above (for isBreedGroupJudgeRing computation)
      const fallbackSlot = judgeSlots.get(fallbackKey);
      const otherSlots = Array.from(judgeSlots.entries()).filter(([k]) => k !== fallbackKey);
      const hasFallbackTiles = (fallbackSlot?.tiles.length || 0) > 0;

      // If fallback judge has no tiles assigned and there are other judges, drop the fallback column
      const orderedSlots =
        !hasFallbackTiles && otherSlots.length > 0
          ? otherSlots.map(([, s]) => s)
          : [fallbackSlot!, ...otherSlots.map(([, s]) => s)].filter(Boolean);

      return orderedSlots.map(({ judge, tiles }) => {
        const existing = currentJudges.find(
          (j) =>
            j.judge_name === judge.judge_name &&
            (j.judge_number || '') === judge.judge_number
        );
        return {
          ...(existing ?? {
            judge_name: judge.judge_name,
            judge_number: judge.judge_number,
            total_hours: 8 as number,
          }),
          dog_classes: tiles,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isUKCConformation,
      ukcConformationCountsLoaded,
      ukcConformationCounts,
      show.breed_group_judges,
      show.breed_judges,
      show.show_elements,
      event.sanctioning_club,
      show.show_type,
    ]
  );

  // ─── syncElementTiles ────────────────────────────────────────────────────────
  /**
   * Ensures all special element tiles (Junior Showmanship, Brace, etc.)
   * exist in the current judges array and have up-to-date entry counts, and
   * removes any Novice Puppy / Veteran / Total Dog / Total Junior tiles.
   * Also adds the BIS blocks (Veteran BIS, BIS, Total Dog, ...) for the selected
   * classes and removes ones whose class is no longer offered. `ringId` keeps a
   * block that already lives in another ring from being added again here.
   *
   * Run whenever judges or scores change (after init). Returns a new judges array
   * when changes are needed, or null when nothing changed.
   */
  const syncElementTiles = useCallback(
    (currentJudges: IRunningOrderBase[], ringId?: string, hiddenBisClasses?: string[]): IRunningOrderBase[] | null => {
      if (!isUKCConformation || currentJudges.length === 0) return null;

      const runningTimePerDog = getRunningTimeMinutes(
        event.sanctioning_club,
        show.show_type,
        undefined,
        undefined
      );

      const showElements: any[] = show.show_elements || [];
      const conformationElements = new Set([
        Constants.dog_class_element.conformation,
        Constants.dog_class_element.conformation_altered,
      ]);

      const scoresForShow = allScores.filter(
        (s: any) => s.show_id?.toString() === show._id?.toString()
      );

      // Junior Showmanship is judged as one class: a single tile with no level, even
      // though entries are stored under the individual junior classes.
      const isJuniorShowmanship = (showElement?: string) =>
        showElement === Constants.dog_class_element.junior_showmanship;

      // Build candidate set from show_elements + legacy scores
      const candidatesMap = new Map<
        string,
        { judge_name: string; judge_number: string; show_element: string; level: string }
      >();

      showElements.forEach((se: any) => {
        if (!se.show_element || conformationElements.has(se.show_element)) return;
        const level = isJuniorShowmanship(se.show_element) ? '' : se.level;
        if (!level && !isJuniorShowmanship(se.show_element)) return;
        if (isUKCConformationUnscheduledLevel(level)) return;
        const k = `${se.show_element}||${se.judge_name || ''}||${se.judge_number || ''}||${level}`;
        candidatesMap.set(k, {
          judge_name: se.judge_name || '',
          judge_number: se.judge_number || '',
          show_element: se.show_element,
          level,
        });
      });

      // Legacy: find element tiles from score records that may not be in show_elements
      scoresForShow.forEach((s: any) => {
        const seVal = s.dog_class?.show_element;
        const level = isJuniorShowmanship(seVal) ? '' : s.dog_class?.level;
        if (!seVal || conformationElements.has(seVal)) return;
        if (!level && !isJuniorShowmanship(seVal)) return;
        if (isUKCConformationUnscheduledLevel(level)) return;
        const k = `${seVal}||${s.judge_name || ''}||${s.judge_number || ''}||${level}`;
        if (!candidatesMap.has(k)) {
          candidatesMap.set(k, {
            judge_name: s.judge_name || '',
            judge_number: s.judge_number || '',
            show_element: seVal,
            level,
          });
        }
      });

      const getEntries = (
        judgeName: string,
        judgeNumber: string,
        showElement: string,
        level: string
      ): number => {
        const inClass = (s: any) =>
          s.dog_class?.show_element === showElement &&
          (isJuniorShowmanship(showElement) || s.dog_class?.level === level);
        const byJudge = scoresForShow.filter(
          (s: any) =>
            (s.judge_name || '') === judgeName &&
            (s.judge_number || '') === judgeNumber &&
            inClass(s)
        );
        if (byJudge.length > 0) return byJudge.length;
        // Fallback for legacy records with no judge on the score
        return scoresForShow.filter(inClass).length;
      };

      let changed = false;
      let updatedJudges = currentJudges.map((j) => {
        const dogClasses = (j.dog_classes || []).filter(
          (dc: any) =>
            !isUKCConformationUnscheduledLevel(dc.level) &&
            !(isJuniorShowmanship(dc.show_element) && dc.level)
        );
        if (dogClasses.length !== (j.dog_classes || []).length) changed = true;
        return { ...j, dog_classes: dogClasses };
      });

      // BIS blocks are one per show, so other rings are passed in to avoid duplicating them here.
      const currentRingId = ringId || '';
      const otherRings = (show.rings || []).filter((r: any) => r.ring !== currentRingId) as any[];
      const syncedRings = syncUKCConformationBISTiles(showElements, [
        { ring: currentRingId, running_order: updatedJudges },
        ...otherRings,
      ], hiddenBisClasses ?? show.hidden_bis_classes ?? []);
      const syncedJudges = syncedRings?.find((r) => r.ring === currentRingId)?.running_order;
      if (syncedJudges) {
        const countTiles = (judges: IRunningOrderBase[]) => judges.reduce((sum, j) => sum + j.dog_classes.length, 0);
        if (countTiles(syncedJudges) !== countTiles(updatedJudges)) changed = true;
        updatedJudges = syncedJudges;
      }

      let nextIndex =
        Math.max(
          -1,
          ...updatedJudges.flatMap((j) =>
            (j.dog_classes || []).map((dc: any) =>
              typeof dc.index === 'number' ? dc.index : -1
            )
          )
        ) + 1;
      updatedJudges.forEach((j) => {
        j.dog_classes.forEach((dc: any) => {
          if (typeof dc.index !== 'number') dc.index = nextIndex++;
        });
      });

      if (candidatesMap.size === 0) return changed ? updatedJudges : null;

      candidatesMap.forEach((candidate) => {
        const foundJudge = updatedJudges.find(
          (j) =>
            j.judge_name === candidate.judge_name &&
            (j.judge_number || '') === candidate.judge_number
        );

        // If the candidate has a specific judge assigned, only add the tile to that judge.
        // If the assigned judge isn't in this ring (e.g. they're in Ring 1 but we're viewing Ring 2),
        // skip the tile entirely — don't fall back to updatedJudges[0].
        // Only fall back to updatedJudges[0] when the candidate has no judge assigned at all.
        const targetJudge = candidate.judge_name
          ? foundJudge  // specific judge: use them or skip
          : (foundJudge ?? updatedJudges[0]); // no specific judge: fall back to first

        if (!targetJudge) return;

        const existing = targetJudge.dog_classes.find(
          (dc: any) =>
            dc.type === Constants.running_order_dog_class_type.dog_class &&
            dc.show_element === candidate.show_element &&
            (dc.level || '') === candidate.level
        );

        const entries = getEntries(
          targetJudge.judge_name || '',
          targetJudge.judge_number || '',
          candidate.show_element,
          candidate.level
        );
        const total_minutes = entries * runningTimePerDog;

        if (existing) {
          if (
            existing.entries !== entries ||
            existing.total_minutes !== total_minutes ||
            existing.duration !== runningTimePerDog
          ) {
            existing.entries = entries;
            existing.total_minutes = total_minutes;
            existing.duration = runningTimePerDog;
            changed = true;
          }
          return;
        }

        targetJudge.dog_classes.push({
          show_element: candidate.show_element,
          level: candidate.level,
          entries,
          total_minutes,
          duration: runningTimePerDog,
          type: Constants.running_order_dog_class_type.dog_class,
          index: nextIndex++,
        });
        changed = true;
      });

      return changed ? updatedJudges : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isUKCConformation,
      show.show_elements,
      show.rings,
      show.hidden_bis_classes,
      show._id,
      show.show_type,
      event.sanctioning_club,
      allScores,
    ]
  );

  return { ukcConformationCounts, ukcConformationCountsLoaded, buildInitialJudges, syncElementTiles };
};
